/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, type ReactNode, createContext, forwardRef, useRef } from 'react';
import { useFocusRing, useHover, usePress } from 'react-aria';
import {
  type ContextValue,
  DEFAULT_SLOT,
  InputContext as InputContextCore,
  Input as InputCore,
  type InputProps as InputCoreProps,
  Provider,
  useContextProps,
  useRenderProps
} from 'react-aria-components';
import { ChevronDownSmallStrokeIcon } from '../../icons/chevron-down-small-stroke/chevron-down-small-stroke.icon';
import { $ } from '../../tokens.stylex';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { IconContext } from '../icon/icon.component';
import { Label, LabelContext } from "../label/label.component";

interface LabeledInputStyleProps {
  /** The variant of the input. (Default: outlined) */
  variant?: keyof typeof variantVariants;
  /** The label of the input. */
  label?: string;
  /** Used to add accessories to the input. */
  children?: ((values: {
    isHovered: boolean;
    isFocused: boolean;
    isFocusVisible: boolean;
    isDisabled: boolean;
    isInvalid: boolean;
    defaultChildren: ReactNode | undefined;
  }) => ReactNode);
  style?: stylex.StyleXStyles;
}

type LabeledInputProps = Omit<InputCoreProps, 'style' | 'className' | 'children'> & LabeledInputStyleProps;

const LabeledInputStyleContext = createContext<ContextValue<LabeledInputStyleProps, HTMLDivElement>>({});

const LabeledInput = forwardRef((props: LabeledInputProps, ref: ForwardedRef<HTMLDivElement>) => {
  let inputRef = useRef<HTMLInputElement>(null);
  let contextProps: InputCoreProps = {};

  [props, ref] = useContextProps(props, ref, LabeledInputStyleContext);
  [contextProps, inputRef] = useContextProps({}, inputRef, InputContextCore);

  const {
    style,
    variant = 'outlined',
    label,
    placeholder,
    children,
    onScroll,
    ...coreProps
  } = props;

  const rawAriaExpanded = contextProps['aria-expanded'] ?? coreProps['aria-expanded'];
  const isExpanded = rawAriaExpanded === true || rawAriaExpanded === 'true';

  const { hoverProps, isHovered } = useHover(coreProps);
  const { isFocused, isFocusVisible, focusProps } = useFocusRing({
    isTextInput: true,
    autoFocus: coreProps.autoFocus,
    within: true,
  });
  // onKeyDown prevents spaces from being entered into the input field
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { pressProps: { onKeyDown, ...pressProps } } = usePress({
    onPressStart: () => inputRef.current?.focus(),
    preventFocusOnPress: true,
  });

  const isInvalid = (!!coreProps['aria-invalid'] && coreProps['aria-invalid'] !== 'false') || (!!contextProps['aria-invalid'] && contextProps['aria-invalid'] !== 'false');
  const isDisabled = coreProps.disabled || contextProps.disabled || false;
  const valueFromProps = coreProps.value ?? contextProps.value;
  const defaultValueFromProps = coreProps.defaultValue ?? contextProps.defaultValue;
  const hasInitialValue = !!valueFromProps || !!defaultValueFromProps;
  const hasValue = hasInitialValue || !!inputRef.current?.value?.length;

  const renderProps = useRenderProps({
    ...coreProps,
    values: {
      isHovered,
      isFocused,
      isFocusVisible,
      isDisabled,
      isInvalid
    },
    defaultChildren: (
      <div {...stylex.props(styles.contentContainer)}>
        <Label />
        <InputCore
          ref={inputRef}
          placeholder={isFocused || hasValue ? placeholder : undefined}
          {...stylex.props(inputStyles.base)}
          onScroll={(event) => {
            if (!isFocused) {
              event.currentTarget.scrollLeft = 0;
            }
            onScroll?.(event);
          }}
          {...coreProps}
        />
      </div>),
    children: (renderProps) => (
      <Provider values={[
        [LabelContext, {
          size: (isFocused || isExpanded || hasValue) ? 12 : 16,
          color: (
            (isDisabled && 'disabled') ||
            (isInvalid && 'critical') ||
            ((isFocused || isExpanded) && 'interactive') ||
            (hasValue && 'tertiary') ||
            (isHovered && 'quaternary') ||
            'placeholder'
          ),
          style: [labelStyles.base, hasInitialValue && labelStyles.noTransition, (isFocused || isExpanded || hasValue) && labelStyles.floating],
          children: label
        }],
        [IconButtonContext, {
          slots: {
            [DEFAULT_SLOT]: {
              variant: 'ghost',
              size: 32,
              style: iconButtonStyles.base,
              isDisabled,
            },
            [LABELED_INPUT_SLOTS.POPOVER_ICON_BUTTON]: {
              'aria-label': 'Show suggestions',
              variant: 'ghost',
              size: 32,
              style: [popoverIconButtonStyles.base, iconButtonStyles.base],
              isDisabled,
              children: (iconButtonRenderProps) => (
                <ChevronDownSmallStrokeIcon
                  color={(
                    (renderProps.isDisabled && 'disabled') ||
                    ((renderProps.isHovered || renderProps.isFocused) && 'interactive') ||
                    'quaternary'
                  )}
                  style={[iconStyles.base, iconButtonRenderProps.isPressed && iconStyles.rotated]}
                />
              )
            }
          }
        }],
        [IconContext, {
          color: (isDisabled && 'disabled') || 'tertiary'
        }]]}>
        {children?.(renderProps) || renderProps.defaultChildren}
      </Provider>
    )
  });

  return (
    <div
      ref={ref}
      data-focused={isFocused || isExpanded || undefined}
      data-disabled={isDisabled || undefined}
      data-hovered={isHovered || undefined}
      data-focus-visible={isFocusVisible || undefined}
      data-invalid={isInvalid || undefined}
      {...pressProps}
      {...hoverProps}
      {...focusProps}
      {...renderProps}
      {...stylex.props(styles.base, variantVariants[variant], style)}>
      {renderProps.children}
    </div>
  );
});

const styles = stylex.create({
  base: {
    borderRadius: 10,
    borderStyle: 'solid',
    overflow: 'hidden',
    alignItems: 'center',
    boxSizing: 'border-box',
    columnGap: 12,
    cursor: {
      default: 'text',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'flex',
    position: 'relative',
    height: 52,
    minWidth: 0,
    paddingBottom: {
      default: 6,
      ':is([data-focused], [data-focus-within], [data-invalid])': 5,
    },
    paddingLeft: {
      default: 14,
      ':is([data-focused], [data-focus-within], [data-invalid])': 13,
    },
    paddingRight: {
      default: 14,
      ':is([data-focused], [data-focus-within], [data-invalid])': 13,
    },
    paddingTop: {
      default: 6,
      ':is([data-focused], [data-focus-within], [data-invalid])': 5,
    },
  },
  contentContainer: {
    flex: '1 1 0',
    cursor: 'inherit',
    display: 'flex',
    position: 'relative',
    height: '100%',
    minWidth: 0,
  }
});

const inputStyles = stylex.create({
  base: {
    borderStyle: 'none',
    outline: 'none',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    caretColor: $.textInteractive,
    color: {
      default: $.textPrimary,
      ':is([data-disabled])': $.textDisabled,
    },
    cursor: 'inherit',
    fontFamily: 'Inter',
    fontFeatureSettings: 'liga off, clig off',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 400,
    letterSpacing: -0.5,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 15,
    width: '100%',
    '::placeholder': { color: $.textPlaceholder },
    '::selection': { backgroundColor: $.surfaceHighlightMedium },
    ':-webkit-autofill': {
      WebkitTextFillColor: $.textPrimary,
      boxShadow: `0 0 0 1000px ${$.surfaceBGPrimary} inset`,
      transition: 'background-color 5000s ease-in-out 0s',
    },
    ':-webkit-autofill:hover': {
      WebkitTextFillColor: $.textPrimary,
      boxShadow: `0 0 0 1000px ${$.surfaceBGPrimary} inset`,
    },
    ':-webkit-autofill:focus': {
      WebkitTextFillColor: $.textPrimary,
      boxShadow: `0 0 0 1000px ${$.surfaceBGPrimary} inset`,
    },
    ':-webkit-autofill:active': {
      WebkitTextFillColor: $.textPrimary,
      boxShadow: `0 0 0 1000px ${$.surfaceBGPrimary} inset`,
    },
  }
});

const labelStyles = stylex.create({
  base: {
    transition: 'font-size 0.15s, top 0.1s ease-in-out',
    backgroundColor: 'transparent',
    cursor: 'inherit',
    fontFamily: 'Inter',
    fontFeatureSettings: 'liga off, clig off',
    fontWeight: 400,
    position: 'absolute',
    transform: 'translate(0, -50%)',
    whiteSpace: 'nowrap',
    top: '50%',
  },
  noTransition: {
    transition: 'none',
  },
  floating: {
    fontWeight: 500,
    top: '23%',
  }
});

const variantVariants = stylex.create({
  outlined: {
    borderColor: {
      default: $.border16,
      ':is([data-disabled])': $.border10,
      ':is([data-focused], [data-focus-within]):not([data-invalid])': $.borderInteractive,
      ':is([data-hovered]):not([data-focused], [data-focus-within], [data-invalid], [data-disabled])': $.border24,
      ':is([data-invalid])': $.borderCritical,
    },
    borderWidth: {
      default: 1,
      ':is([data-disabled])': 1,
      ':is([data-focused], [data-focus-within], [data-invalid])': 2,
    },
    backgroundColor: $.surfaceBGPrimary,
  },
  filled: {
    borderColor: {
      default: 'transparent',
      ':is([data-focused], [data-focus-within]):not([data-invalid])': $.borderInteractive,
    },
    borderWidth: {
      default: 1,
      ':is([data-focused], [data-focus-within], [data-invalid])': 2,
    },
    backgroundColor: {
      default: $.opacity06,
      ':is([data-focused], [data-focus-within]):not([data-invalid])': $.surfaceBGPrimary,
      ':is([data-invalid])': $.surfaceCriticalSilent,
    },
  },
});

const popoverIconButtonStyles = stylex.create({
  base: {
    backgroundColor: 'transparent'
  }
});

const iconButtonStyles = stylex.create({
  base: {
    marginRight: -4,
  }
});

const iconStyles = stylex.create({
  base: {
    transition: '0.15s',
  },
  rotated: {
    transform: 'rotate(180deg)',
    transformOrigin: 'center center',
  }
});

const LABELED_INPUT_SLOTS = {
  POPOVER_ICON_BUTTON: 'popover-icon-button'
};

export { LABELED_INPUT_SLOTS, LabeledInput, LabeledInputStyleContext, type LabeledInputProps, type LabeledInputStyleProps };
